import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {UserPlus, Search, Check, X, Clock, Users, Briefcase, Star, ChevronRight, Ban, Mail, Phone, MapPin, FileText, GraduationCap, Briefcase as BriefcaseIcon, Globe, ExternalLink, ZoomIn} from 'lucide-react'
import EmpresaLayout from '@/components/EmpresaLayout'
import { lumi } from '@/lib/lumi'
import { useAuth } from '@/hooks/useAuth'
import { useLinks, type WorkLink } from '@/hooks/useLinks'

const STATUS_META: Record<string, { label: string; cls: string }> = {
  pendiente: { label: 'Pendiente', cls: 'bg-amber-50 text-amber-700' },
  aceptada: { label: 'Activa', cls: 'bg-emerald-50 text-emerald-700' },
  rechazada: { label: 'Rechazada', cls: 'bg-red-50 text-red-600' },
  finalizada: { label: 'Finalizada', cls: 'bg-slate-100 text-slate-600' },
}

interface CollabProfile {
  _id?: string
  userId?: string
  fullName?: string
  email?: string
  phone?: string
  photoUrl?: string
  city?: string
  state?: string
  address?: string
  education?: string
  experience?: string
  skills?: string[]
  certifications?: string[]
  languages?: string[]
  salaryClaim?: string | number
  cvUrl?: string
  linkedinUrl?: string
  githubUrl?: string
  portfolioUrl?: string
  headline?: string
}

function Avatar({ name, photo, size = 10, onView }: { name: string; photo?: string; size?: number; onView?: (url: string) => void }) {
  const sz = `h-${size} w-${size}`
  if (photo) {
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onView?.(photo) }}
        className={`${sz} group relative shrink-0 rounded-full ${onView ? 'cursor-zoom-in' : ''}`}
        title={onView ? 'Clic para ampliar' : undefined}
      >
        <img
          src={photo}
          alt={name}
          className={`${sz} rounded-full object-cover border-2 border-white shadow-sm`}
        />
        {onView && (
          <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/0 text-white opacity-0 transition group-hover:bg-black/35 group-hover:opacity-100">
            <ZoomIn size={size > 12 ? 18 : 13} />
          </span>
        )}
      </button>
    )
  }
  return (
    <div className={`${sz} flex items-center justify-center rounded-full bg-amber-400 text-sm font-bold text-slate-900 shrink-0`}>
      {(name || 'C').charAt(0).toUpperCase()}
    </div>
  )
}

function ProfileModal({ collab, onClose }: { collab: CollabProfile; onClose: () => void }) {
  const [photoLightbox, setPhotoLightbox] = useState(false)
  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="relative flex w-full max-w-2xl flex-col rounded-2xl bg-white shadow-2xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start gap-4 bg-slate-900 px-6 py-5">
          <div className="relative shrink-0">
            <Avatar name={collab.fullName || 'C'} photo={collab.photoUrl} size={16} onView={() => setPhotoLightbox(true)} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-white">{collab.fullName || 'Sin nombre'}</h2>
            {collab.headline && <p className="mt-0.5 text-sm text-amber-300">{collab.headline}</p>}
            <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-400">
              {collab.email && (
                <span className="flex items-center gap-1"><Mail size={12} /> {collab.email}</span>
              )}
              {collab.phone && (
                <span className="flex items-center gap-1"><Phone size={12} /> {collab.phone}</span>
              )}
              {(collab.city || collab.state) && (
                <span className="flex items-center gap-1">
                  <MapPin size={12} /> {[collab.city, collab.state].filter(Boolean).join(', ')}
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800 shrink-0">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* Datos generales */}
          <div className="grid grid-cols-2 gap-3">
            {collab.education && (
              <InfoBox icon={<GraduationCap size={14} />} label="Escolaridad" value={collab.education} />
            )}
            {collab.experience && (
              <InfoBox icon={<BriefcaseIcon size={14} />} label="Experiencia" value={collab.experience} />
            )}
            {collab.salaryClaim && (
              <InfoBox icon={<Star size={14} />} label="Pretención salarial mensual" value={`$${Number(collab.salaryClaim).toLocaleString()}`} />
            )}
            {collab.address && (
              <InfoBox icon={<MapPin size={14} />} label="Dirección" value={collab.address} />
            )}
          </div>

          {/* Habilidades */}
          {collab.skills && collab.skills.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-semibold text-slate-600 uppercase tracking-wide">Habilidades</p>
              <div className="flex flex-wrap gap-2">
                {collab.skills.map((s) => (
                  <span key={s} className="rounded-full bg-amber-50 border border-amber-200 px-3 py-1 text-xs font-medium text-amber-800">{s}</span>
                ))}
              </div>
            </div>
          )}

          {/* Idiomas */}
          {collab.languages && collab.languages.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-semibold text-slate-600 uppercase tracking-wide">Idiomas</p>
              <div className="flex flex-wrap gap-2">
                {collab.languages.map((l) => (
                  <span key={l} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">{l}</span>
                ))}
              </div>
            </div>
          )}

          {/* Certificaciones */}
          {collab.certifications && collab.certifications.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-semibold text-slate-600 uppercase tracking-wide">Certificaciones</p>
              <div className="flex flex-wrap gap-2">
                {collab.certifications.map((c) => (
                  <span key={c} className="rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-xs font-medium text-emerald-800">{c}</span>
                ))}
              </div>
            </div>
          )}

          {/* Links profesionales */}
          {(collab.linkedinUrl || collab.githubUrl || collab.portfolioUrl) && (
            <div>
              <p className="mb-2 text-xs font-semibold text-slate-600 uppercase tracking-wide">Perfiles profesionales</p>
              <div className="flex flex-wrap gap-2">
                {collab.linkedinUrl && (
                  <a href={collab.linkedinUrl} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100">
                    <Globe size={12} /> LinkedIn <ExternalLink size={11} />
                  </a>
                )}
                {collab.githubUrl && (
                  <a href={collab.githubUrl} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100">
                    <Globe size={12} /> GitHub <ExternalLink size={11} />
                  </a>
                )}
                {collab.portfolioUrl && (
                  <a href={collab.portfolioUrl} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-100">
                    <Globe size={12} /> Portafolio <ExternalLink size={11} />
                  </a>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-100 px-6 py-4 flex justify-between items-center">
          {collab.cvUrl ? (
            <a
              href={collab.cvUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-semibold text-amber-400 transition hover:bg-slate-800"
            >
              <FileText size={15} /> Descargar CV
            </a>
          ) : (
            <span className="text-xs text-slate-400">CV no disponible</span>
          )}
          <button onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">
            Cerrar
          </button>
        </div>
      </div>
    </div>

    {/* Lightbox foto colaborador */}
    {photoLightbox && collab.photoUrl && (
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4"
        onClick={() => setPhotoLightbox(false)}
      >
        <div className="relative max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => setPhotoLightbox(false)}
            className="absolute -top-10 right-0 flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-sm text-white hover:bg-white/20"
          >
            <X size={15} /> Cerrar
          </button>
          <img
            src={collab.photoUrl}
            alt={collab.fullName || 'Colaborador'}
            className="mx-auto max-h-[80vh] w-auto rounded-2xl shadow-2xl"
          />
        </div>
      </div>
    )}
    </>
  )
}

function InfoBox({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-lg bg-slate-50 p-3">
      <p className="flex items-center gap-1 text-xs text-slate-400">{icon} {label}</p>
      <p className="mt-0.5 text-sm font-semibold text-slate-800">{value}</p>
    </div>
  )
}

// Mapa de foto y nombre completo por collaboratorUserId para solicitudes pendientes
type CollabMeta = { name: string; photo?: string; email?: string }

function EmpresaColaboradores() {
  const { user } = useAuth()
  const { links, loading, fetchByCompany, createLink, updateLink } = useLinks()
  const [companyName, setCompanyName] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [position, setPosition] = useState('')
  const [area, setArea] = useState('')
  const [inviting, setInviting] = useState(false)
  const [feedback, setFeedback] = useState<{ type: 'ok' | 'err'; msg: string } | null>(null)

  // Mapa de metadatos de colaborador (foto + nombre) para solicitudes pendientes
  const [collabMeta, setCollabMeta] = useState<Record<string, CollabMeta>>({})

  // Modal de perfil completo
  const [profileModal, setProfileModal] = useState<CollabProfile | null>(null)
  const [loadingProfile, setLoadingProfile] = useState(false)

  // Lightbox de foto a nivel de página (para tarjetas de listas)
  const [pageLightbox, setPageLightbox] = useState<string | null>(null)

  useEffect(() => {
    if (user) {
      fetchByCompany(user.userId)
      lumi.entities.companies
        .list({ filter: { userId: user.userId }, limit: 1 })
        .then((r) => {
          if (r.list.length > 0) setCompanyName(r.list[0].tradeName || '')
        })
        .catch((e) => console.error('Error al cargar empresa:', e))
    }
  }, [user])

  // Cuando cambian los links, cargar foto y nombre de pendientes
  useEffect(() => {
    const pendingLinks = links.filter((l) => l.status === 'pendiente')
    if (pendingLinks.length === 0) return

    const missing = pendingLinks.filter((l) => l.collaboratorUserId && !collabMeta[l.collaboratorUserId])
    if (missing.length === 0) return

    Promise.all(
      missing.map((l) =>
        lumi.entities.collaborators
          .list({ filter: { userId: l.collaboratorUserId }, limit: 1 })
          .then((r) => {
            const c = r.list[0]
            if (c) return { id: l.collaboratorUserId, meta: { name: c.fullName || c.email || l.collaboratorEmail || '', photo: c.photoUrl, email: c.email } }
            return null
          })
          .catch(() => null)
      )
    ).then((results) => {
      const updates: Record<string, CollabMeta> = {}
      results.forEach((r) => { if (r) updates[r.id] = r.meta })
      if (Object.keys(updates).length > 0) setCollabMeta((prev) => ({ ...prev, ...updates }))
    })
  }, [links])

  const openProfile = async (collaboratorUserId: string) => {
    setLoadingProfile(true)
    setProfileModal({})
    try {
      const r = await lumi.entities.collaborators.list({ filter: { userId: collaboratorUserId }, limit: 1 })
      if (r.list.length > 0) {
        setProfileModal(r.list[0] as CollabProfile)
      }
    } catch (e) {
      console.error('Error al cargar perfil:', e)
    } finally {
      setLoadingProfile(false)
    }
  }

  const handleInvite = async () => {
    if (!user || !searchQuery.trim()) {
      setFeedback({ type: 'err', msg: 'Ingresa el nombre o correo del colaborador.' })
      return
    }
    if (!position.trim()) {
      setFeedback({ type: 'err', msg: 'El campo Puesto es obligatorio.' })
      return
    }
    setInviting(true)
    setFeedback(null)
    try {
      const q = searchQuery.trim().toLowerCase()
      // Buscar primero por correo exacto, luego por nombre parcial
      const [byEmail, byName] = await Promise.all([
        lumi.entities.collaborators.list({ filter: { email: searchQuery.trim() }, limit: 1 }),
        lumi.entities.collaborators.list({ limit: 200 }),
      ])
      let collab = byEmail.list[0]
      if (!collab) {
        collab = (byName.list as Array<Record<string, unknown>>).find(
          (c) => (c.fullName as string || '').toLowerCase().includes(q)
        ) as typeof collab
      }
      if (!collab) {
        setFeedback({ type: 'err', msg: 'No se encontró ningún colaborador con ese nombre o correo en la plataforma.' })
        setInviting(false)
        return
      }
      const exists = links.find(
        (l) => l.collaboratorUserId === collab.userId && (l.status === 'pendiente' || l.status === 'aceptada')
      )
      if (exists) {
        setFeedback({ type: 'err', msg: 'Ya existe una vinculacion activa o pendiente con este colaborador.' })
        setInviting(false)
        return
      }
      await createLink({
        companyUserId: user.userId,
        companyName,
        collaboratorUserId: collab.userId,
        collaboratorName: collab.fullName,
        collaboratorEmail: collab.email,
        position: position.trim(),
        area: area.trim(),
        status: 'pendiente',
        requestedBy: 'empresa',
        creator: user.userId,
      })
      setSearchQuery('')
      setPosition('')
      setArea('')
      setFeedback({ type: 'ok', msg: `Solicitud de vinculación enviada a ${collab.fullName || collab.email}.` })
      fetchByCompany(user.userId)
    } catch (e) {
      console.error('Error al invitar colaborador:', e)
      setFeedback({ type: 'err', msg: 'Ocurrio un error al enviar la solicitud.' })
    } finally {
      setInviting(false)
    }
  }

  const changeStatus = async (link: WorkLink, status: WorkLink['status']) => {
    try {
      const extra: Partial<WorkLink> = { status }
      if (status === 'aceptada' && !link.startDate) extra.startDate = new Date().toISOString()
      if (status === 'finalizada') extra.endDate = new Date().toISOString()
      await updateLink(link._id, extra)
      if (user) fetchByCompany(user.userId)
    } catch (e) {
      console.error('Error al actualizar vinculacion:', e)
      alert('No se pudo actualizar la vinculacion.')
    }
  }

  const pending = links.filter((l) => l.status === 'pendiente')
  const active = links.filter((l) => l.status === 'aceptada')
  const past = links.filter((l) => l.status === 'rechazada' || l.status === 'finalizada')

  return (
    <EmpresaLayout
      title="Colaboradores"
      subtitle="Gestiona la vinculacion laboral con tus colaboradores"
    >
      {/* Enlace a postulantes */}
      <Link
        to="/empresa/postulantes"
        className="mb-6 flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 transition hover:border-amber-300"
      >
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-900 text-amber-400">
          <Briefcase size={18} />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-slate-900">Ver postulantes a vacantes</p>
          <p className="text-xs text-slate-500">Revisa candidatos que se postularon a tus vacantes publicadas</p>
        </div>
        <ChevronRight size={16} className="text-slate-400" />
      </Link>

      {/* Invitar colaborador */}
      <section className="mb-8 rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="mb-1 flex items-center gap-2 text-base font-semibold text-slate-900">
          <UserPlus size={18} className="text-amber-500" /> Vincular colaborador
        </h2>
        <p className="mb-4 text-sm text-slate-500">
          Busca al colaborador por nombre o por correo electrónico. El puesto es obligatorio.
        </p>
        <div className="grid gap-3 sm:grid-cols-3">
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Nombre o correo del colaborador"
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
          <div className="relative">
            <input
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              placeholder="Puesto *"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-100"
            />
            <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-red-400 font-semibold">*</span>
          </div>
          <input
            value={area}
            onChange={(e) => setArea(e.target.value)}
            placeholder="Área (opcional)"
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>
        {feedback && (
          <p className={`mt-3 text-sm ${feedback.type === 'ok' ? 'text-emerald-600' : 'text-red-500'}`}>
            {feedback.msg}
          </p>
        )}
        <button
          onClick={handleInvite}
          disabled={inviting}
          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-amber-400 px-5 py-2 text-sm font-semibold text-slate-900 transition hover:bg-amber-300 disabled:opacity-60"
        >
          <Search size={15} /> {inviting ? 'Enviando...' : 'Enviar solicitud'}
        </button>
      </section>

      {/* Solicitudes pendientes */}
      {pending.length > 0 && (
        <section className="mb-8">
          <h2 className="mb-3 flex items-center gap-2 text-base font-semibold text-slate-900">
            <Clock size={18} className="text-amber-500" /> Solicitudes pendientes
          </h2>
          <div className="space-y-3">
            {pending.map((link) => {
              const meta = collabMeta[link.collaboratorUserId] || null
              const displayName = meta?.name || link.collaboratorName || link.collaboratorEmail || 'Colaborador'
              const photo = meta?.photo
              return (
                <div key={link._id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50/40 p-4">
                  <div className="flex items-center gap-3">
                    <Avatar name={displayName} photo={photo} size={10} onView={(url) => setPageLightbox(url)} />
                    <div>
                      <button
                        onClick={() => openProfile(link.collaboratorUserId)}
                        className="text-sm font-semibold text-slate-900 hover:text-amber-600 hover:underline text-left"
                      >
                        {displayName}
                      </button>
                      <p className="text-xs text-slate-500">
                        {link.position || 'Sin puesto'}{' '}
                        {link.requestedBy === 'colaborador' ? '· solicitada por el colaborador' : '· enviada por ti'}
                      </p>
                    </div>
                  </div>
                  {link.requestedBy === 'colaborador' ? (
                    <div className="flex gap-2">
                      <button onClick={() => changeStatus(link, 'aceptada')} className="inline-flex items-center gap-1 rounded-lg bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-600">
                        <Check size={13} /> Aceptar
                      </button>
                      <button onClick={() => changeStatus(link, 'rechazada')} className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50">
                        <X size={13} /> Rechazar
                      </button>
                    </div>
                  ) : (
                    <span className="rounded-md bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-700">Esperando respuesta</span>
                  )}
                </div>
              )
            })}
          </div>
        </section>
      )}

      {/* Vinculaciones activas */}
      <section className="mb-8">
        <h2 className="mb-3 flex items-center gap-2 text-base font-semibold text-slate-900">
          <Users size={18} className="text-amber-500" /> Colaboradores activos
        </h2>
        {loading && active.length === 0 ? (
          <p className="text-sm text-slate-400">Cargando...</p>
        ) : active.length === 0 ? (
          <p className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
            Aun no tienes colaboradores vinculados.
          </p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {active.map((link) => (
              <div key={link._id} className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex items-center gap-3">
                  <Avatar name={link.collaboratorName || 'C'} size={10} />
                  <div className="min-w-0 flex-1">
                    <button
                      onClick={() => openProfile(link.collaboratorUserId)}
                      className="truncate text-sm font-semibold text-slate-900 hover:text-amber-600 hover:underline text-left w-full"
                    >
                      {link.collaboratorName || link.collaboratorEmail}
                    </button>
                    <p className="truncate text-xs text-slate-500">{link.position || 'Colaborador'}</p>
                  </div>
                  <span className={`rounded-md px-2 py-0.5 text-xs font-medium ${STATUS_META.aceptada.cls}`}>Activa</span>
                </div>
                <div className="mt-3 flex gap-2">
                  <Link to="/empresa/evaluaciones" className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-slate-900 py-1.5 text-xs font-semibold text-amber-400 hover:bg-slate-800">
                    <Star size={13} /> Evaluar
                  </Link>
                  <button onClick={() => changeStatus(link, 'finalizada')} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50">
                    <Ban size={13} /> Finalizar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Historial */}
      {past.length > 0 && (
        <section>
          <h2 className="mb-3 text-base font-semibold text-slate-900">Historial</h2>
          <div className="space-y-2">
            {past.map((link) => (
              <div key={link._id} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3">
                <div className="flex items-center gap-3">
                  <Avatar name={link.collaboratorName || 'C'} size={8} />
                  <div>
                    <button
                      onClick={() => openProfile(link.collaboratorUserId)}
                      className="text-sm font-medium text-slate-800 hover:text-amber-600 hover:underline text-left"
                    >
                      {link.collaboratorName || link.collaboratorEmail}
                    </button>
                    <p className="text-xs text-slate-400">{link.position || 'Colaborador'}</p>
                  </div>
                </div>
                <span className={`rounded-md px-2.5 py-1 text-xs font-medium ${STATUS_META[link.status]?.cls || ''}`}>
                  {STATUS_META[link.status]?.label || link.status}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Lightbox de foto (tarjetas de listas) */}
      {pageLightbox && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4"
          onClick={() => setPageLightbox(null)}
        >
          <div className="relative max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setPageLightbox(null)}
              className="absolute -top-10 right-0 flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-sm text-white hover:bg-white/20"
            >
              <X size={15} /> Cerrar
            </button>
            <img
              src={pageLightbox}
              alt="Colaborador"
              className="mx-auto max-h-[80vh] w-auto rounded-2xl shadow-2xl"
            />
          </div>
        </div>
      )}

      {/* Modal perfil completo */}
      {profileModal !== null && (
        loadingProfile ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="rounded-2xl bg-white px-10 py-8 text-center shadow-2xl">
              <p className="text-sm font-medium text-slate-600">Cargando perfil...</p>
            </div>
          </div>
        ) : (
          <ProfileModal collab={profileModal} onClose={() => setProfileModal(null)} />
        )
      )}
    </EmpresaLayout>
  )
}

export default EmpresaColaboradores
